import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { NettStatusTable } from '../components/ui/nett-status-table';
import { formatQuantity as formatNumber } from '@/utils/formatters';
import { formatDate } from '../lib/utils';
import { 
  ShoppingCart, Package, ChevronDown, ChevronUp, 
  RefreshCw, Loader2, Calendar, ClipboardList, TrendingDown, 
  FileText, AlertTriangle, Factory, RotateCcw, CheckSquare
} from 'lucide-react';
import IndentCreationDialog from '../components/IndentCreationDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PurchaseFulfillmentPage = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ 
    total_items: 0, so_buy_count: 0, wo_material_count: 0, reorder_count: 0,
    rmt_count: 0, sfg_count: 0, fng_count: 0, total_shortage: 0 
  });
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [expandedItem, setExpandedItem] = useState(null); // Single expanded item key (accordion style)
  const [liveNettStatus, setLiveNettStatus] = useState(null);
  const [loadingNettStatus, setLoadingNettStatus] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});
  
  // Multi-Indent Creation state
  const [multiIndentDialogOpen, setMultiIndentDialogOpen] = useState(false);
  const [indentItemsData, setIndentItemsData] = useState([]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchFulfillmentItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (sourceFilter !== 'all') params.append('source_filter', sourceFilter);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const response = await axios.get(`${API_URL}/purchase-fulfillment${queryString}`, { 
        headers: getAuthHeader() 
      });
      setItems(response.data.items || []);
      setSummary(response.data.summary || {});
      setSelectedItems({});
    } catch (error) {
      console.error('Error fetching fulfillment items:', error);
      toast.error('Failed to load purchase fulfillment items');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, sourceFilter]);

  useEffect(() => {
    fetchFulfillmentItems();
  }, [fetchFulfillmentItems]);

  const toggleItemExpand = async (itemId, sourceType) => {
    const key = `${itemId}-${sourceType}`;
    if (expandedItem === key) {
      setExpandedItem(null);
      setLiveNettStatus(null);
      return;
    }
    setExpandedItem(key);
    setLiveNettStatus(null);
    setLoadingNettStatus(true);
    try {
      const response = await axios.get(`${API_URL}/items/${itemId}/nett-status`, { headers: getAuthHeader() });
      const data = response.data;
      const cur = data.stock_position?.current || {};
      setLiveNettStatus({
        stock_in_hand: cur.stock_in_hand || 0,
        wip: cur.wip || 0,
        ohwip: cur.ohwip || 0,
        indent_qty: cur.indent_qty || 0,
        po_issued: cur.po_issued || 0,
        blocked: cur.blocked || 0,
        so_blocked: cur.so_blocked || 0,
        nett: cur.nett_status || 0,
      });
    } catch (error) {
      console.error('Error fetching live nett status:', error);
    } finally {
      setLoadingNettStatus(false);
    }
  };

  const toggleItemSelect = (item) => {
    const key = `${item.item_id}-${item.source_type}`;
    setSelectedItems(prev => ({
      ...prev,
      [key]: prev[key] ? undefined : item
    }));
  };

  const toggleSelectAll = () => {
    const allSelected = items.every(item => selectedItems[`${item.item_id}-${item.source_type}`]);
    if (allSelected) {
      setSelectedItems({});
    } else {
      const newSelected = {};
      items.forEach(item => {
        newSelected[`${item.item_id}-${item.source_type}`] = item;
      });
      setSelectedItems(newSelected);
    }
  };

  const getSelectedCount = () => {
    return Object.values(selectedItems).filter(Boolean).length;
  };

  const openMultiIndentDialog = () => {
    const selected = Object.values(selectedItems).filter(Boolean);
    if (selected.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    
    // Prepare data for the unified IndentCreationDialog
    // Map the purchase fulfillment item structure to the dialog's expected format
    const indentData = selected.map(item => ({
      item_id: item.item_id,
      item_name: item.item_name,
      item_code: item.item_code,
      unit: item.unit || 'NOS',
      source_type: item.item_source_type || 'MP', // Item's MP/IP type for display
      lead_time: item.lead_time || 0,
      nett_status: item.nett_status,
      location_code: item.location_code || '',
      location_id: item.location_id || '',
      default_qty: item.shortage,
      // SO selection - collect available SOs from linked sources
      available_sos: (item.source_type === 'so_buy' || item.source_type === 'wo_material')
        ? item.linked_sources?.map(s => ({
            id: s.sales_order_id,
            so_number: s.so_number,
            number: s.so_number,
            client_name: s.customer_name || '',
            delivery_date: s.delivery_date || '',
            pending_qty: s.pending_qty || s.quantity || 0
          })).filter(s => s.id) || []
        : [],
      default_so_id: item.earliest_so?.sales_order_id || '',
      default_so_number: item.earliest_so?.so_number || ''
    }));
    
    setIndentItemsData(indentData);
    setMultiIndentDialogOpen(true);
  };

  const handleIndentSuccess = () => {
    setSelectedItems({});
    fetchFulfillmentItems();
  };

  const getSourceBadge = (sourceType) => {
    switch(sourceType) {
      case 'so_buy':
        return <Badge className="bg-blue-100 text-blue-700">SO Buy</Badge>;
      case 'wo_material':
        return <Badge className="bg-purple-100 text-purple-700"><Factory className="h-3 w-3 mr-1" />WO Material</Badge>;
      case 'reorder':
        return <Badge className="bg-amber-100 text-amber-700"><RotateCcw className="h-3 w-3 mr-1" />Reorder</Badge>;
      default:
        return <Badge variant="secondary">{sourceType}</Badge>;
    }
  };

  const getCategoryBadge = (category) => {
    // Normalize to uppercase
    const cat = category?.toUpperCase() || '';
    const colorMap = {
      'RMT': 'bg-blue-100 text-blue-800',
      'SFG': 'bg-yellow-100 text-yellow-800',
      'FNG': 'bg-green-100 text-green-800',
      'CTM': 'bg-purple-100 text-purple-800',
      'ASSET': 'bg-orange-100 text-orange-800'
    };
    return <Badge className={colorMap[cat] || 'bg-slate-100 text-slate-800'}>{cat || category}</Badge>;
  };

  return (
    <DashboardLayout>
    <div className="p-6 space-y-6" data-testid="purchase-fulfillment-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShoppingCart className="h-8 w-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Purchase Fulfillment Register</h1>
            <p className="text-sm text-slate-500">One-stop page for all Buy requirements - SO Buy, WO Materials, Stock Reorder</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchFulfillmentItems}
            disabled={loading}
            data-testid="refresh-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
          {getSelectedCount() > 0 && (
            <Button 
              onClick={openMultiIndentDialog}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-indents-btn"
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              Create Indent ({getSelectedCount()})
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-7 gap-3">
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <ClipboardList className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Total</p>
              <p className="text-xl font-bold text-slate-800">{summary.total_items}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">SO Buy</p>
              <p className="text-xl font-bold text-blue-700">{summary.so_buy_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50">
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Factory className="h-4 w-4 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">WO Material</p>
              <p className="text-xl font-bold text-purple-700">{summary.wo_material_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <RotateCcw className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Reorder</p>
              <p className="text-xl font-bold text-amber-700">{summary.reorder_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Package className="h-4 w-4 text-slate-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">RMT</p>
              <p className="text-xl font-bold text-slate-800">{summary.rmt_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Package className="h-4 w-4 text-slate-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">SFG</p>
              <p className="text-xl font-bold text-slate-800">{summary.sfg_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrendingDown className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Total Shortage</p>
              <p className="text-xl font-bold text-red-600">{formatNumber(summary.total_shortage)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <Label className="text-sm">Source:</Label>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[150px]" data-testid="source-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="so_buy">SO Buy</SelectItem>
              <SelectItem value="wo_material">WO Material</SelectItem>
              <SelectItem value="reorder">Reorder</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm">Category:</Label>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[150px]" data-testid="category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="RMT">Raw Material</SelectItem>
              <SelectItem value="SFG">Semi Finished</SelectItem>
              <SelectItem value="FNG">Finished Goods</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto text-sm text-slate-500">
          {getSelectedCount() > 0 && `${getSelectedCount()} item(s) selected`}
        </div>
      </div>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-slate-500">
              <Package className="h-12 w-12 mb-4 text-slate-300" />
              <p className="text-lg font-medium">No items with shortage</p>
              <p className="text-sm">All purchase requirements are fulfilled</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10">
                    <Checkbox 
                      checked={items.length > 0 && items.every(item => selectedItems[`${item.item_id}-${item.source_type}`])}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-center">Lead Time</TableHead>
                  <TableHead className="text-right">Nett Status</TableHead>
                  <TableHead className="text-right">Shortage</TableHead>
                  <TableHead>Linked SO/WO</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const itemKey = `${item.item_id}-${item.source_type}`;
                  const isExpanded = expandedItem === itemKey;
                  const isSelected = !!selectedItems[itemKey];
                  
                  return (
                    <React.Fragment key={itemKey}>
                      <TableRow 
                        className={`cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-emerald-50' : ''}`}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox 
                            checked={isSelected}
                            onCheckedChange={() => toggleItemSelect(item)}
                          />
                        </TableCell>
                        <TableCell onClick={() => toggleItemExpand(item.item_id, item.source_type)}>
                          <div>
                            <p className="font-medium">{item.item_name}</p>
                          </div>
                        </TableCell>
                        <TableCell onClick={() => toggleItemExpand(item.item_id, item.source_type)}>
                          {getCategoryBadge(item.category)}
                        </TableCell>
                        <TableCell onClick={() => toggleItemExpand(item.item_id, item.source_type)}>
                          {getSourceBadge(item.source_type)}
                        </TableCell>
                        <TableCell className="text-center" onClick={() => toggleItemExpand(item.item_id, item.source_type)}>
                          <span className="text-slate-600">{item.lead_time}d</span>
                        </TableCell>
                        <TableCell className="text-right" onClick={() => toggleItemExpand(item.item_id, item.source_type)}>
                          <span className={item.nett_status < 0 ? 'text-red-600 font-medium' : 'text-slate-600'}>
                            {formatNumber(item.nett_status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right" onClick={() => toggleItemExpand(item.item_id, item.source_type)}>
                          <span className="text-red-600 font-bold">{formatNumber(item.shortage)}</span>
                        </TableCell>
                        <TableCell onClick={() => toggleItemExpand(item.item_id, item.source_type)}>
                          {item.earliest_so?.so_number ? (
                            <span className="text-blue-600">{item.earliest_so.so_number}</span>
                          ) : item.source_type === 'reorder' ? (
                            <span className="text-amber-600">Min: {formatNumber(item.min_level)}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell onClick={() => toggleItemExpand(item.item_id, item.source_type)}>
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Details */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={9} className="bg-slate-50 p-4">
                            <div className="space-y-4">
                              {/* Nett Status Breakdown — live recalculated */}
                              <div>
                                <h4 className="font-medium mb-2 text-sm">
                                  Nett Status Breakdown
                                  {loadingNettStatus && <Loader2 className="inline h-3 w-3 animate-spin ml-2" />}
                                  {liveNettStatus && !loadingNettStatus && (
                                    <span className="text-xs font-normal text-green-600 ml-2">Live</span>
                                  )}
                                </h4>
                                <NettStatusTable 
                                  nettStatus={liveNettStatus || item.nett_status_breakdown || {}}
                                  compact={true}
                                />
                              </div>
                              
                              {/* Reorder Info */}
                              {item.source_type === 'reorder' && (
                                <div className="bg-amber-50 p-3 rounded-lg">
                                  <h4 className="font-medium mb-2 text-sm text-amber-700">Reorder Info</h4>
                                  <div className="grid grid-cols-4 gap-4 text-sm">
                                    <div>
                                      <span className="text-slate-500">Min Level:</span>
                                      <span className="ml-2 font-medium">{formatNumber(item.min_level)}</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">Reorder Qty:</span>
                                      <span className="ml-2 font-medium">{formatNumber(item.reorder_qty)}</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">Current Nett:</span>
                                      <span className="ml-2 font-medium text-red-600">{formatNumber(item.nett_status)}</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">Shortfall:</span>
                                      <span className="ml-2 font-medium text-red-600">{formatNumber(item.shortage)}</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Linked Sources Table */}
                              {item.linked_sources && item.linked_sources.length > 0 && item.source_type !== 'reorder' && (
                                <div>
                                  <h4 className="font-medium mb-2 text-sm">
                                    {item.source_type === 'so_buy' ? 'Linked Sales Orders' : 'Linked Work Orders'}
                                  </h4>
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-white">
                                        <TableHead>#</TableHead>
                                        <TableHead>{item.source_type === 'so_buy' ? 'SO Number' : 'WO Number'}</TableHead>
                                        <TableHead>{item.source_type === 'so_buy' ? 'Customer' : 'Parent Item'}</TableHead>
                                        <TableHead className="text-right">Required</TableHead>
                                        <TableHead className="text-right">Pending</TableHead>
                                        <TableHead>Delivery</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {item.linked_sources.map((src, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell className="text-slate-400">{idx + 1}</TableCell>
                                          <TableCell className="font-medium">
                                            {item.source_type === 'so_buy' ? src.so_number : src.work_order_number}
                                          </TableCell>
                                          <TableCell>
                                            {item.source_type === 'so_buy' ? src.customer_name : src.parent_item_name}
                                          </TableCell>
                                          <TableCell className="text-right">
                                            {formatNumber(item.source_type === 'so_buy' ? src.quantity : src.required_qty)}
                                          </TableCell>
                                          <TableCell className="text-right text-orange-600 font-medium">
                                            {formatNumber(item.source_type === 'so_buy' ? src.pending_qty : src.pending_qty)}
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-1">
                                              <Calendar className="h-3 w-3 text-slate-400" />
                                              {formatDate(src.delivery_date || src.target_production_date)}
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Unified Indent Creation Dialog */}
      <IndentCreationDialog
        open={multiIndentDialogOpen}
        onOpenChange={setMultiIndentDialogOpen}
        items={indentItemsData}
        onSuccess={handleIndentSuccess}
        title="Create Indents"
        description="Create purchase requisitions for selected items from Purchase Fulfillment Register."
        desiredPoDateRequired={true}
        sourceContext="purchase_fulfillment"
      />
    </div>
    </DashboardLayout>
  );
};

export default PurchaseFulfillmentPage;
